// Snapshots / Periods list page (A/S/U).
//
// Owns the top-level Period close landing page. Shows up to 24 months of
// periods with their status (open / locked), totals, freeze date, frozen-by.
// Clicking a row navigates to /snapshots/<period> (period detail) or
// /snapshots/<period>/preview if not yet frozen.

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { snapshots as snapshotsApi } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { formatBalance as formatMoney } from '@/lib/format';
import type { SnapshotsForPeriodResponse } from '@frt/shared-types';

const PERIODS_TO_SHOW = 12;

function listPeriods(monthsBack: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const period = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    out.push(period);
  }
  return out;
}

export function SnapshotPeriodsPage() {
  const { data: me } = useMe();
  const periods = useMemo(() => listPeriods(PERIODS_TO_SHOW), []);

  // One query per period. Inexpensive (12 small reads, all cacheable) and
  // keeps each row independently re-fetchable when a freeze lands.
  const queries = useQueries({
    queries: periods.map((p) => ({
      queryKey: ['snapshots', p] as const,
      queryFn: () => snapshotsApi.forPeriod(p),
      staleTime: 30_000,
    })),
  });

  const canFreeze = me && (me.role === 'admin' || me.role === 'senior');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Period Close</h1>
        <p className="text-sm text-slate-500">
          Locked periods are immutable; admins may amend individual snapshots.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Periods</CardTitle>
        </CardHeader>
        <CardBody>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-2 pr-3">Period</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Snapshots</th>
                <th className="py-2 pr-3 text-right">Total balance</th>
                <th className="py-2 pr-3">Frozen at</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {periods.map((period, idx) => {
                const q = queries[idx];
                const data = q?.data as SnapshotsForPeriodResponse | undefined;
                const locked = !!(data && data.data.length > 0);
                return (
                  <tr key={period} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-mono">{period}</td>
                    <td className="py-2 pr-3">
                      {q?.isLoading ? (
                        <span className="text-slate-400">…</span>
                      ) : locked ? (
                        <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Open
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {data?.totals.count ?? 0}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {data ? formatMoney(data.totals.balance_amount_sum) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">
                      {data?.frozen_at ? formatDate(data.frozen_at) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {locked ? (
                        <Link
                          to="/snapshots/$period"
                          params={{ period }}
                          className="text-brand-700 hover:underline"
                        >
                          View
                        </Link>
                      ) : canFreeze ? (
                        <Link
                          to="/snapshots/$period/preview"
                          params={{ period }}
                          className="text-brand-700 hover:underline"
                        >
                          Preview
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

// Whole-dollar balance formatting comes from `@/lib/format` (imported above
// as `formatMoney`).

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

